import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { Paperplane } from "../../files/icons/index.js";
import { Button, Status } from "./component.js";
import ClaimFormModal, {
  buildInitialForm,
  getClaimFormIssues,
} from "./claimFormModal.js";

function formatCharges(val) {
  if (val === undefined || val === null || val === "") return "—";
  const n = parseFloat(val);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(val);
}

function readinessLabel(issues, rowStatus) {
  if (rowStatus === "submitted") return { color: "green", text: "Submitted" };
  if (rowStatus === "submitting") return { color: "blue", text: "Submitting…" };
  if (rowStatus === "error") return { color: "red", text: "Failed" };
  if (issues.isIncomplete) {
    const parts = [];
    if (issues.missingCount > 0) {
      parts.push(`${issues.missingCount} missing`);
    }
    if (issues.mbiInvalid) parts.push("invalid MBI");
    if (issues.invalidZipCount > 0 && issues.missingCount === 0) {
      parts.push("invalid ZIP");
    }
    return { color: "orange", text: parts.join(" · ") || "Incomplete" };
  }
  return { color: "purple", text: "Complete" };
}

function BatchClaimReviewModal({
  claims,
  orgSettings,
  period,
  onClose,
  onSubmitted,
}) {
  const [drafts, setDrafts] = useState(() => {
    const next = {};
    for (const claim of claims) {
      next[claim.id] = buildInitialForm(claim, orgSettings);
    }
    return next;
  });
  const [rowStatus, setRowStatus] = useState(() => {
    const next = {};
    for (const claim of claims) {
      next[claim.id] = "idle";
    }
    return next;
  });
  const [rowErrors, setRowErrors] = useState({});
  const [editingClaim, setEditingClaim] = useState(null);
  const [submittingBatch, setSubmittingBatch] = useState(false);

  const claimById = useMemo(() => {
    const map = {};
    for (const claim of claims) map[claim.id] = claim;
    return map;
  }, [claims]);

  const remainingClaims = useMemo(
    () => claims.filter((c) => rowStatus[c.id] !== "submitted"),
    [claims, rowStatus]
  );

  const completeIds = useMemo(() => {
    return remainingClaims
      .filter((c) => {
        const form = drafts[c.id];
        if (!form) return false;
        if (rowStatus[c.id] === "submitting") return false;
        return !getClaimFormIssues(form).isIncomplete;
      })
      .map((c) => c.id);
  }, [remainingClaims, drafts, rowStatus]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (!submittingBatch) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [submittingBatch]);

  function succeededIds() {
    return Object.entries(rowStatus)
      .filter(([, status]) => status === "submitted")
      .map(([id]) => id);
  }

  function handleClose() {
    if (submittingBatch) return;
    const done = succeededIds();
    onClose(done.length ? done : undefined);
  }

  function saveDraft(claimId, form) {
    setDrafts((prev) => ({ ...prev, [claimId]: form }));
    setRowStatus((prev) =>
      prev[claimId] === "error" ? { ...prev, [claimId]: "idle" } : prev
    );
    setRowErrors((prev) => {
      if (!prev[claimId]) return prev;
      const next = { ...prev };
      delete next[claimId];
      return next;
    });
    setEditingClaim(null);
  }

  async function submitAllComplete() {
    if (submittingBatch || completeIds.length === 0) return;
    setSubmittingBatch(true);

    let hadFailure = false;
    const newlySucceeded = [];
    const alreadySubmitted = Object.entries(rowStatus)
      .filter(([, status]) => status === "submitted")
      .map(([id]) => id);

    for (const claimId of completeIds) {
      const claim = claimById[claimId];
      const form = drafts[claimId];
      if (!claim || !form) continue;

      setRowStatus((prev) => ({ ...prev, [claimId]: "submitting" }));
      setRowErrors((prev) => {
        if (!prev[claimId]) return prev;
        const next = { ...prev };
        delete next[claimId];
        return next;
      });

      try {
        const payload = {
          ...form,
          patientID: claim.id,
          patientName: `${claim.firstName} ${claim.lastName}`,
          period,
        };
        const { data } = await axios.post("/api/admin/claims/submit", payload);
        if (data.claimStatus === "submitted") {
          setRowStatus((prev) => ({ ...prev, [claimId]: "submitted" }));
          newlySucceeded.push(claimId);
        } else {
          hadFailure = true;
          setRowStatus((prev) => ({ ...prev, [claimId]: "error" }));
          setRowErrors((prev) => ({
            ...prev,
            [claimId]: data.errors || ["Submission failed."],
          }));
        }
      } catch {
        hadFailure = true;
        setRowStatus((prev) => ({ ...prev, [claimId]: "error" }));
        setRowErrors((prev) => ({
          ...prev,
          [claimId]: ["Submission failed. Please try again."],
        }));
      }

      if (onSubmitted) onSubmitted();
    }

    setSubmittingBatch(false);

    const allSubmittedIds = [...alreadySubmitted, ...newlySucceeded];
    const batchFullyDone =
      !hadFailure &&
      newlySucceeded.length > 0 &&
      claims.every((c) => allSubmittedIds.includes(c.id));

    if (batchFullyDone) {
      onClose(allSubmittedIds);
    }
  }

  const completeCount = completeIds.length;

  return (
    <>
      <div className="fixed inset-0 z-2000 flex items-center justify-center bg-[#80808094]">
        <div className="w-full max-w-5xl px-8">
          <div
            className="card shadowmore relative"
            onClick={(e) => e.stopPropagation()}
          >
            {submittingBatch && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#80808094]">
                <div className="d-flex flex-column align-items-center">
                  <div
                    className="spin-icon"
                    style={{
                      width: 40,
                      height: 40,
                      border: "3px solid #e5e5e5",
                      borderTopColor: "#3b82f6",
                      borderRadius: "50%",
                    }}
                  />
                  <div className="typo-normal-150 mt-3 text-white">Submitting claims…</div>
                </div>
              </div>
            )}
            <div className="card-header flex items-center justify-between">
              <span className="typo-header-3xLarge">Review Batch Claims</span>
              <span className="typo-normal-150 text-muted">
                {remainingClaims.length} patient
                {remainingClaims.length === 1 ? "" : "s"}
                {completeCount > 0
                  ? ` · ${completeCount} ready to submit`
                  : ""}
              </span>
            </div>

            <div className="card-content max-h-[70vh] overflow-y-auto">
              <div className="card-header sm">
                <div className="row align-items-center">
                  <div className="col-xs-3">
                    <span className="typo-strong-150">Patient</span>
                  </div>
                  <div className="col-xs-1 text-center">
                    <span className="typo-strong-150">G-Code</span>
                  </div>
                  <div className="col-xs-2 text-center">
                    <span className="typo-strong-150">Date of Service</span>
                  </div>
                  <div className="col-xs-1 text-center">
                    <span className="typo-strong-150">Charges</span>
                  </div>
                  <div className="col-xs-3">
                    <span className="typo-strong-150">Readiness</span>
                  </div>
                  <div className="col-xs-2"></div>
                </div>
              </div>

              {remainingClaims.map((claim, index) => {
                const form = drafts[claim.id] || {};
                const issues = getClaimFormIssues(form);
                const status = rowStatus[claim.id] || "idle";
                const badge = readinessLabel(issues, status);
                const errors = rowErrors[claim.id] || [];
                const isLast = index === remainingClaims.length - 1;
                const canEdit = status !== "submitting" && !submittingBatch;

                return (
                  <div
                    key={claim.id}
                    className={"card-header sm " + (isLast ? "last" : "")}
                    style={{ minHeight: 56 }}
                  >
                    <div className="row align-items-center">
                      <div className="col-xs-3">
                        <div className="typo-strong-150">
                          {claim.firstName} {claim.lastName}
                        </div>
                        {claim.mbi && (
                          <div className="typo-normal-125 text-muted">
                            {claim.mbi}
                          </div>
                        )}
                      </div>
                      <div className="col-xs-1 text-center">
                        <div className="typo-strong-150">
                          {form.procedureCode || claim.procedureCode || "—"}
                        </div>
                      </div>
                      <div className="col-xs-2 text-center">
                        <div className="typo-normal-150">
                          {form.dateOfService || "—"}
                        </div>
                      </div>
                      <div className="col-xs-1 text-center">
                        <div className="typo-normal-150">
                          {formatCharges(form.charges)}
                        </div>
                      </div>
                      <div className="col-xs-3">
                        <Status
                          color={badge.color}
                          text={badge.text}
                          size="small"
                          muted
                        />
                        {errors.length > 0 && (
                          <div className="typo-normal-125 text-red-600 mt-1">
                            {errors[0]}
                            {errors.length > 1
                              ? ` (+${errors.length - 1} more)`
                              : ""}
                          </div>
                        )}
                      </div>
                      <div className="col-xs-2 text-end">
                        <Button
                          text="Edit"
                          color="grey"
                          size="sm"
                          muted
                          disabled={!canEdit}
                          onClick={() => setEditingClaim(claim)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {remainingClaims.length === 0 && (
                <div className="card-header sm last">
                  <div className="typo-normal-150 text-muted">
                    All selected claims were submitted.
                  </div>
                </div>
              )}
            </div>

            <div className="card-content d-flex justify-content-end align-items-center">
              {completeCount === 0 && remainingClaims.length > 0 && (
                <span className="typo-normal-125 text-red-500 me-3">
                  Complete required fields before submitting
                </span>
              )}
              <Button
                onClick={handleClose}
                color="grey"
                muted={true}
                text="Cancel"
                className="me-2"
                disabled={submittingBatch}
              />
              <Button
                onClick={submitAllComplete}
                color="blue"
                text={
                  submittingBatch
                    ? "Submitting…"
                    : `Submit ${completeCount} complete`
                }
                leftIcon={
                  <Paperplane height={12} width={12} color="white" />
                }
                disabled={submittingBatch || completeCount === 0}
              />
            </div>
          </div>
        </div>
      </div>

      {editingClaim && (
        <ClaimFormModal
          key={editingClaim.id}
          claim={editingClaim}
          initialForm={drafts[editingClaim.id]}
          submitting={false}
          onClose={() => setEditingClaim(null)}
          onSubmit={(form) => saveDraft(editingClaim.id, form)}
          orgSettings={orgSettings}
          submitErrors={[]}
          title={`Edit Claim — ${editingClaim.firstName} ${editingClaim.lastName}`}
          submitLabel="Save"
          elevated
          allowIncomplete
        />
      )}
    </>
  );
}

export default BatchClaimReviewModal;
